import { useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ulid } from 'ulid';
import { useLocation } from 'react-router-dom';

import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { useGo } from '@/hooks/useResourceNavigation';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useEntityForm } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import type {
	CreatePricingPolicyMutation,
	CreatePricingPolicyMutationVariables
} from '@/graphql/generated';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { PricingPolicyForm } from './pricing-policy-form';
import {
	buildCreatePricingPolicyInput,
	pricingPolicyFormSchema,
	type PricingPolicyFormValues
} from './pricing-policy-input';
import { PRICING_POLICY_CREATE_MUTATION } from './pricing-policy-update.gql';
import { BILLING_CYCLES_LIST_QUERY } from './lookup.gql';
import { PRICING_POLICIES_LIST_QUERY } from './pricing-policies.gql';

interface CloneData {
	name?: string;
	priority?: number;
	isActive?: boolean;
	scenarioId?: string;
	scenarioLabel?: string; // Label for scenario autocomplete
	tariffId?: string;
	tariffLabel?: string; // Label for tariff autocomplete
	overstayTariffId?: string;
	overstayTariffLabel?: string; // Label for overstay tariff autocomplete
	limitId?: string;
	limitLabel?: string; // Label for limit autocomplete
	billingCycleId?: number; // Keep as number from GraphQL, convert to string for form
}

interface LocationState {
	cloneData?: CloneData;
}

export const PricingPoliciesCreate = () => {
	const go = useGo();
	const location = useLocation();
	const cloneData = (location.state as LocationState | null)?.cloneData;
	const { setPageHeader, resetPageHeader } = usePageHeader();

	const form = useEntityForm<PricingPolicyFormValues>({
		mode: cloneData ? 'clone' : 'create',
		schema: pricingPolicyFormSchema,
		defaultValues: {
			name: cloneData?.name ?? '',
			priority: cloneData?.priority?.toString() ?? '',
			isActive: cloneData?.isActive ?? true,
			scenarioId: cloneData?.scenarioId ?? '',
			tariffId: cloneData?.tariffId ?? '',
			overstayTariffId: cloneData?.overstayTariffId ?? '',
			limitId: cloneData?.limitId ?? '',
			billingCycleId: cloneData?.billingCycleId?.toString() ?? ''
		}
	});

	const idempotentPricingPolicyId = useMemo(() => ulid(), []);

	const {
		data: billingCyclesData,
		loading: loadingBillingCycles,
		error: billingCyclesError
	} = useQuery(BILLING_CYCLES_LIST_QUERY);

	const { data: pricingPoliciesData } = useQuery(PRICING_POLICIES_LIST_QUERY, {
		variables: { first: 100 }
	});

	const lookupsLoading = loadingBillingCycles;
	const lookupError = billingCyclesError;

	// Use delayed loading to prevent flashing "Unable to load form data" message
	const { shouldShowLoading } = useDelayedLoading(lookupsLoading, {
		delay: 300,
		showInitialLoading: false
	});

	const billingCycles = billingCyclesData?.billingCycles ?? [];

	const existingPriorities = useMemo(() => {
		const nodes = pricingPoliciesData?.pricingPolicies?.nodes ?? [];
		return nodes
			.filter((node: any) => node.priority != null)
			.map((node: any) => ({
				priority: node.priority,
				label: node.name
			}));
	}, [pricingPoliciesData]);

	const [createPricingPolicy, { loading: isSubmitting }] = useMutation<
		CreatePricingPolicyMutation,
		CreatePricingPolicyMutationVariables
	>(PRICING_POLICY_CREATE_MUTATION);

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	// Set up page header
	useEffect(() => {
		setPageHeader({
			title: 'Create Pricing Policy',
			description: 'Create a new pricing policy for your system',
			showBackButton: true,
			onBackClick: () => go({ to: '/pricing-policies' })
		});

		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, go]);

	const handleSubmit = async (values: PricingPolicyFormValues) => {
		let input;

		try {
			input = buildCreatePricingPolicyInput(
				values,
				idempotentPricingPolicyId
			);
		} catch (error) {
			showMutationErrorToast('pricing-policies', 'create', { error });
			return;
		}

		try {
			const { data } = await createPricingPolicy(
				getMutationOptions('pricing-policies', { input })
			);

			const message = extractCheckResultError(data?.createPricingPolicy);

			if (message) {
				showMutationErrorToast('pricing-policies', 'create', { message });
				return;
			}

			showMutationSuccessToast('pricing-policies', 'create');
			go({
				to: `/pricing-policies?success=${idempotentPricingPolicyId}&mode=create`
			});
		} catch (error) {
			showMutationErrorToast('pricing-policies', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			{lookupError && !shouldShowLoading && (
				<Alert variant="destructive">
					<AlertTitle>Unable to load form data</AlertTitle>
					<AlertDescription>
						{lookupError.message ??
							'Some lookup values could not be loaded.'}
					</AlertDescription>
				</Alert>
			)}

			{shouldShowLoading ? (
				<Card>
					<div className="text-sm text-muted-foreground">
						Loading form data...
					</div>
				</Card>
			) : (
				<PricingPolicyForm
					mode={cloneData ? 'clone' : 'create'}
					form={form}
					billingCycles={billingCycles}
					existingPriorities={existingPriorities}
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
					initialTariffLabel={cloneData?.tariffLabel}
					initialOverstayTariffLabel={cloneData?.overstayTariffLabel}
					initialLimitLabel={cloneData?.limitLabel}
					initialScenarioLabel={cloneData?.scenarioLabel}
					headerTitle={cloneData ? 'Clone Pricing Policy' : 'Create Pricing Policy'}
				/>
			)}
		</FormPageLayout>
	);
};
